import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  PlusCircleIcon,
  PhotoIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
  CommandLineIcon,
  BookOpenIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import WalletConnect from './WalletConnect';

const navigation = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Create', href: '/create', icon: PlusCircleIcon },
  { name: 'Programs', href: '/programs', icon: CommandLineIcon },
  { name: 'Tools', href: '/tools', icon: WrenchScrewdriverIcon },
  { name: 'Guide', href: '/guide', icon: BookOpenIcon },
];

export default function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-vextaris-darker via-vextaris-dark to-vextaris-darker">
      {/* Navigation */}
      <nav className="border-b border-gray-800/80 bg-vextaris-dark/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-vextaris flex items-center justify-center">
                <span className="text-xl font-bold text-white">V</span>
              </div>
              <div>
                <div className="text-xl font-bold text-white">VexForge</div>
                <div className="text-xs text-gray-400">Powered by Vextaris</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-vextaris-purple text-white'
                        : 'text-gray-300 hover:bg-vextaris-purple/10 hover:text-white'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>

            {/* Wallet Connect */}
            <div className="flex items-center space-x-4">
              <WalletConnect />

              {/* Mobile menu button */}
              <button
                type="button"
                className="lg:hidden rounded-lg p-2 text-gray-400 hover:bg-vextaris-purple/10 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-vextaris-purple/20">
            <div className="space-y-1 px-4 pb-3 pt-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-base font-medium ${
                      isActive
                        ? 'bg-vextaris-purple text-white'
                        : 'text-gray-300 hover:bg-vextaris-purple/10 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/80 mt-16">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-500">
              &copy; 2026 Vexidus Labs Ltd. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <a href="https://docs.vextaris.io" className="text-sm text-gray-500 hover:text-white transition-colors">
                Docs
              </a>
              <a href="https://vexscan.io" className="text-sm text-gray-500 hover:text-white transition-colors">
                Explorer
              </a>
              <a href="https://x.com/vextaris" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}